<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { formatPeriodLabel } from 'kanban/helper/reportFormatters';
import {
  TIMESERIES_METRICS,
  TIMESERIES_GROUP_BY,
  CHART_PRIMARY_COLOR,
} from 'kanban/constants/reports';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  metric: {
    type: String,
    default: 'throughput',
  },
  groupBy: {
    type: String,
    default: 'day',
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry', 'update:metric', 'update:groupBy']);

const { t } = useI18n();

const metricLabel = key =>
  t(`KANBAN.REPORTS.THROUGHPUT.METRIC.${key.toUpperCase()}`);
const groupByLabel = key =>
  t(`KANBAN.REPORTS.THROUGHPUT.GROUP_BY.${key.toUpperCase()}`);

const metricOptions = computed(() =>
  TIMESERIES_METRICS.map(value => ({ value, label: metricLabel(value) }))
);
const groupByOptions = computed(() =>
  TIMESERIES_GROUP_BY.map(value => ({ value, label: groupByLabel(value) }))
);

const points = computed(() => props.data?.data || []);

const isEmpty = computed(
  () => !points.value.length || points.value.every(point => !point.value)
);

const labelFor = isoDate =>
  formatPeriodLabel(isoDate, props.groupBy, props.locale);

// O componente de barras passou a ser o da biblioteca de gráficos do fornecedor, que lê
// `categories` e `series` em vez do formato antigo de coleção. Passar o formato antigo não
// dá erro: o gráfico simplesmente aparece vazio.
const chartData = computed(() => ({
  categories: points.value.map(point => labelFor(point.date)),
  series: [
    {
      id: props.metric,
      label: metricLabel(props.metric),
      color: CHART_PRIMARY_COLOR,
      data: points.value.map(point => point.value),
    },
  ],
}));
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.THROUGHPUT.TITLE')"
    :info-text="t('KANBAN.REPORTS.THROUGHPUT.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="isEmpty"
    @retry="emit('retry')"
  >
    <template #actions>
      <SelectMenu
        :model-value="metric"
        :options="metricOptions"
        :label="metricLabel(metric)"
        max-width="max-w-48"
        sub-menu-position="bottom"
        @update:model-value="emit('update:metric', $event)"
      />
      <SelectMenu
        :model-value="groupBy"
        :options="groupByOptions"
        :label="groupByLabel(groupBy)"
        max-width="max-w-36"
        sub-menu-position="bottom"
        @update:model-value="emit('update:groupBy', $event)"
      />
    </template>
    <div class="h-72">
      <BarChart
        :data="chartData"
        :aria-label="t('KANBAN.REPORTS.THROUGHPUT.TITLE')"
        :height="288"
      />
    </div>
  </KanbanReportWidget>
</template>
